import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import type {RowData, Table as TanStackTable} from "@tanstack/react-table";
import {SERVER_TABLE_EXPANSION_COLUMN_ID} from "./ServerTableExpansionColumn";
import {
    getColumnAlignment,
    getPinnedColumnSx,
    getVisibleColumns
} from "./utils";

const MAX_SKELETON_ROW_COUNT = 10;

interface ServerTableSkeletonProps<TData extends RowData> {
    table: TanStackTable<TData>
}

const ServerTableSkeleton = <TData extends RowData,>({
    table
}: ServerTableSkeletonProps<TData>) => {
    const visibleColumns = getVisibleColumns(table);
    // Never promise more rows than the page can deliver: a small page would otherwise
    // resolve from ten placeholder rows down to its real height.
    const skeletonRowCount = Math.min(
        table.getState().pagination.pageSize || MAX_SKELETON_ROW_COUNT,
        MAX_SKELETON_ROW_COUNT
    );

    return (
        <>
            {Array.from({length: skeletonRowCount}, (_, rowIndex) => (
                <Box component="tr" key={`skeleton-${rowIndex}`}>
                    {visibleColumns.map((column, columnIndex) => {
                        const alignment = getColumnAlignment(column);
                        const skeletonWidth = 48 + ((rowIndex + columnIndex) % 4) * 10;
                        const isExpansionColumn = column.id === SERVER_TABLE_EXPANSION_COLUMN_ID;

                        return (
                            <Box
                                component="td"
                                key={column.id}
                                sx={{
                                    px: 1,
                                    ...getPinnedColumnSx(column, 1),
                                    textAlign: alignment
                                }}
                            >
                                {!isExpansionColumn && (
                                    <Skeleton
                                        variant="rounded"
                                        animation="wave"
                                        sx={{
                                            width: `${skeletonWidth}%`,
                                            height: 14,
                                            ml: alignment === 'left' ? 0 : 'auto',
                                            mr: alignment === 'right' ? 0 : 'auto'
                                        }}
                                    />
                                )}
                            </Box>
                        );
                    })}
                </Box>
            ))}
        </>
    );
};

export default ServerTableSkeleton;
